import React from 'react';

/**
 * Converte duração em minutos totais.
 * Aceita: número (minutos), "HH:MM:SS", "HH:MM", "MM"
 */
function toMinutes(duration) {
    if (!duration) return 0;
    if (typeof duration === 'number') return duration;

    const str = String(duration).trim();
    const parts = str.split(':');
    const totalMinutes = parts.length >= 2
        ? parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10)
        : parseInt(str, 10);

    return isNaN(totalMinutes) ? 0 : totalMinutes;
}

/**
 * Converte duração em texto legível.
 * Ex: "01:30:00" → "1h 30min" | "00:50:00" → "50 min" | 90 → "1h 30min"
 */
function formatDuration(duration) {
    const totalMinutes = toMinutes(duration);
    if (totalMinutes <= 0) return null;

    const hours = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;

    if (hours > 0 && mins > 0) return `${hours}h ${mins}min`;
    if (hours > 0) return `${hours}h`;
    return `${mins} min`;
}

const formatPrice = (value) =>
    `R$ ${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * SelectionSummary — linha discreta com a duração total e, quando todos os
 * serviços marcados têm preço, o valor total. Só aparece com mais de um marcado.
 */
function SelectionSummary({ selected }) {
    if (selected.length < 2) return null;

    const totalMinutes = selected.reduce((sum, proc) => sum + toMinutes(proc.duration), 0);
    const allPriced = selected.every(proc => proc.price != null && proc.price > 0);
    const totalPrice = allPriced ? selected.reduce((sum, proc) => sum + Number(proc.price), 0) : null;
    const totalDuration = formatDuration(totalMinutes);

    return (
        <div className="ps-proc-summary">
            <span className="ps-proc-summary__count">{selected.length} serviços marcados</span>
            {totalDuration && (
                <span className="ps-proc-summary__item">Duração total: {totalDuration}</span>
            )}
            {totalPrice != null && (
                <span className="ps-proc-summary__item ps-proc-summary__item--price">Total: {formatPrice(totalPrice)}</span>
            )}
        </div>
    );
}

/**
 * StepProcedure — Step 1
 * Seleção de um ou mais serviços com cards premium (ícone badge, nome, duração, preço).
 * `selected` é a lista marcada; `onToggle` marca/desmarca um serviço.
 */
export default function StepProcedure({ procedures, selected, onToggle, onNext, onBack }) {
    const selectedIds = new Set(selected.map(proc => proc.id));

    return (
        <div className="ps-step-content">
            <h2 className="ps-step-title">Escolha os serviços</h2>
            <p className="ps-step-subtitle">Você pode marcar mais de um para fazer na mesma visita.</p>

            <div className="ps-proc-list">
                {procedures.map(proc => {
                    const isSelected = selectedIds.has(proc.id);
                    return (
                        <div
                            key={proc.id}
                            className={`ps-proc-card${isSelected ? ' ps-proc-card--selected' : ''}`}
                            onClick={() => onToggle(proc)}
                            role="checkbox"
                            aria-checked={isSelected}
                        >
                            {/* Ícone badge */}
                            <div className="ps-proc-card__icon-badge">
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={isSelected ? '#ED3680' : '#aaa'} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2z" />
                                    <path d="M12 8v4l3 3" />
                                </svg>
                            </div>

                            {/* Info */}
                            <div className="ps-proc-card__info">
                                <span className="ps-proc-card__name">{proc.name}</span>
                                {formatDuration(proc.duration) && (
                                    <span className="ps-proc-card__duration">
                                        Duração aproximada: {formatDuration(proc.duration)}
                                    </span>
                                )}
                                {proc.description && (
                                    <span className="ps-proc-card__duration">{proc.description}</span>
                                )}
                                {proc.price != null && proc.price > 0 && (
                                    <span className="ps-proc-card__price">{formatPrice(proc.price)}</span>
                                )}
                            </div>

                            {/* Check icon */}
                            {isSelected && (
                                <div className="ps-proc-card__check">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                        <circle cx="12" cy="12" r="12" fill="#ED3680" />
                                        <path d="M7 12l4 4 6-6" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            <SelectionSummary selected={selected} />

            <div className="ps-actions">
                <button className="ps-btn ps-btn--secondary" onClick={onBack}>
                    ← Voltar
                </button>
                <button
                    className="ps-btn ps-btn--primary"
                    disabled={selected.length === 0}
                    onClick={onNext}
                >
                    Continuar
                </button>
            </div>

            <p className="ps-trust-note">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
                Seus dados estão protegidos
            </p>
        </div>
    );
}
